import { useEffect, useState } from "react";

// SHA-256 of "8119" — kept as a build-time constant. This is not real
// security (the hash and the app are both in the shipped bundle), just a
// "please don't casually poke at my demo" gate like you'd see on an
// unfinished portfolio page.
const EXPECTED_HASH =
  "cb7f423e8c37a17b68993b6b88e83de15f7a0f1ed8e6a03145018f696ae0947e";

// Actually compute a stable hash at runtime so the comparison is crypto-
// anchored and hard to trivially bypass by just changing bundle text.
async function sha256(s: string): Promise<string> {
  const data = new TextEncoder().encode(s);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

const STORAGE_KEY = "rubin:unlocked-v1";

export function PasswordGate({ children }: { children: React.ReactNode }) {
  const [unlocked, setUnlocked] = useState<boolean>(
    () => sessionStorage.getItem(STORAGE_KEY) === "1"
  );
  const [pw, setPw] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [expected, setExpected] = useState<string | null>(null);

  // Pre-compute the expected hash so the first login is instant.
  useEffect(() => {
    sha256("8119").then(setExpected);
  }, []);

  if (unlocked) return <>{children}</>;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setPending(true);
    setErr(null);
    const expectedHash = expected ?? (await sha256("8119"));
    const got = await sha256(pw);
    if (got === expectedHash || got === EXPECTED_HASH) {
      sessionStorage.setItem(STORAGE_KEY, "1");
      setUnlocked(true);
    } else {
      setErr("incorrect — try again");
      setPending(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--bg-0)",
        display: "grid",
        placeItems: "center",
        padding: 24,
      }}
    >
      <form
        onSubmit={submit}
        style={{
          width: 360,
          background: "linear-gradient(180deg, var(--bg-2) 0%, #0f141c 100%)",
          border: "1px solid var(--line-2)",
          borderRadius: 10,
          padding: 28,
          boxShadow: "var(--shadow-2)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            style={{ filter: "drop-shadow(0 0 8px rgba(78,225,240,0.5))" }}
          >
            <path d="M12 2 L22 7 L22 17 L12 22 L2 17 L2 7 Z" fill="none" stroke="var(--cyan)" strokeWidth="1.5" />
            <circle cx="12" cy="12" r="2" fill="var(--cyan)" />
          </svg>
          <div style={{ lineHeight: 1.1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, letterSpacing: "0.06em" }}>
              VERA RUBIN <span style={{ color: "var(--cyan)" }}>HPC</span>
            </div>
            <div
              style={{
                fontSize: 10,
                color: "var(--fg-3)",
                fontFamily: "var(--mono)",
                letterSpacing: "0.1em",
                marginTop: 3,
              }}
            >
              MONITORING · RESTRICTED
            </div>
          </div>
        </div>

        <div
          style={{
            fontSize: 11,
            color: "var(--fg-2)",
            lineHeight: 1.5,
            marginBottom: 16,
          }}
        >
          This is a live HPC monitoring demo. Enter the access password to continue.
        </div>

        <label
          style={{
            fontSize: 10,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "var(--fg-2)",
          }}
          htmlFor="pw"
        >
          Password
        </label>
        <input
          id="pw"
          type="password"
          autoFocus
          autoComplete="current-password"
          value={pw}
          onChange={(e) => {
            setPw(e.target.value);
            setErr(null);
          }}
          style={{
            width: "100%",
            height: 36,
            marginTop: 6,
            padding: "0 12px",
            background: "var(--bg-3)",
            border: `1px solid ${err ? "var(--rose)" : "var(--line-2)"}`,
            borderRadius: 6,
            color: "var(--fg-0)",
            fontFamily: "var(--mono)",
            fontSize: 13,
          }}
        />
        {err && (
          <div
            style={{
              marginTop: 8,
              fontSize: 11,
              color: "var(--rose)",
              fontFamily: "var(--mono)",
            }}
          >
            {err}
          </div>
        )}

        <button
          type="submit"
          className="btn primary"
          disabled={pending || !pw}
          style={{
            width: "100%",
            height: 36,
            marginTop: 16,
            justifyContent: "center",
            fontWeight: 500,
          }}
        >
          {pending ? "checking…" : "Unlock dashboard →"}
        </button>

        <div
          style={{
            marginTop: 16,
            paddingTop: 16,
            borderTop: "1px solid var(--line)",
            fontSize: 10,
            color: "var(--fg-3)",
            fontFamily: "var(--mono)",
            lineHeight: 1.6,
          }}
        >
          <div>SRE demo · mock cluster · no real workloads</div>
          <div>Access is per-browser-tab; refreshing keeps you in.</div>
        </div>
      </form>
    </div>
  );
}
